import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSidebar } from '../../SidebarContext';
import { Pen, Trash2, X } from 'lucide-react';
import { decryptToken } from '../utils/getToken';
import { getDecryptedUserFromCookie } from '../utils/getCookie';

const CreateJob = () => {
    const [formData, setFormData] = useState({
        role: '',
        experience: '',
        salary: '',
        jobDescription: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [editingJobId, setEditingJobId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [jobToDelete, setJobToDelete] = useState(null);
    const { isSidebarOpen } = useSidebar();
    const token = decryptToken("accessToken") || ""
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");

    const apiUrl = process.env.REACT_APP_LINK;

    const fetchJobs = async () => {
        try {
            const res = await fetch(`${apiUrl}/jobs`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            if (!res.ok) {
                const text = await res.text();
                console.error("Failed to fetch jobs. Server response:", text);
                toast.error("Failed to fetch jobs.");
                return;
            }

            const data = await res.json();
            if (Array.isArray(data)) {
                setJobs(data);
            } else if (Array.isArray(data.data)) {
                setJobs(data.data);
            } else {
                toast.error("Invalid job data format.");
                setJobs([]);
            }
        } catch (error) {
            console.error("Fetch error:", error);
            toast.error("Server error");
            setJobs([]);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const method = editingJobId ? 'PUT' : 'POST';
        const url = editingJobId
            ? `${apiUrl}/jobs/${editingJobId}`
            : `${apiUrl}/jobs`;

        const toastId = toast.loading('⏳ Submitting job...');

        try {
            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setFormData({ role: '', experience: '', salary: '', jobDescription: '' });
                setEditingJobId(null);
                setShowForm(false);
                fetchJobs();

                toast.update(toastId, {
                    render: editingJobId ? 'Job updated successfully!' : 'Job created successfully!',
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                    closeOnClick: true
                });
            } else {
                toast.update(toastId, {
                    render: 'Server error.',
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000
                });
            }
        } catch (error) {
            toast.update(toastId, {
                render: '❌ Server error.',
                type: 'error',
                isLoading: false,
                autoClose: 3000
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (job) => {
        setFormData({
            role: job.role,
            experience: job.experience,
            salary: job.salary,
            jobDescription: job.jobDescription
        });
        setEditingJobId(job.id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        setJobToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            await fetch(`${apiUrl}/jobs/${jobToDelete}`, { method: 'DELETE',
                headers:{
                     "Authorization":`Bearer ${token}`
                }
             });
            fetchJobs();
            toast.success('Job deleted successfully!');
        } catch (error) {
            console.error("Failed to delete job", error);
            toast.error('Failed to delete job.');
        } finally {
            setShowDeleteModal(false);
            setJobToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setJobToDelete(null);
    };

    return (
        <div className={`open-close ${isSidebarOpen ? 'open' : 'closed'}`}>
            <div>
                <div className="oms-header">
                    <h1 className="dashboard-title">Jobs</h1>
                    <button
                        className="oms-add-btn"
                        onClick={() => { setShowForm(true); setEditingJobId(null); }}
                    >
                        Create Job
                    </button>
                </div>

                {/* Form Modal */}
                {showForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 z-[99999] flex flex-col justify-end items-center">
                        <div className="bg-white w-full max-h-[90vh] max-w-[600px] rounded-t-2xl shadow-xl p-5 overflow-y-auto transition-transform duration-300 animate-slide-up">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold text-gray-800">
                                    {editingJobId ? 'Edit Job' : 'Add New Job'}
                                </h2>
                                <button
                                    onClick={() => setShowForm(false)}
                                    className="text-gray-500 hover:text-red-500 text-sm font-medium"
                                >
                                    <X />
                                </button>
                            </div>
                            <form onSubmit={handleSubmit} className="space-y-3 text-sm">
                                <div>
                                    <label className="block text-gray-600 mb-1">Job Role</label>
                                    <input
                                        name="role"
                                        placeholder="Enter job role"
                                        value={formData.role}
                                        onChange={handleChange}
                                        required
                                        className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-2 focus:ring-blue-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-600 mb-1">Experience (in years)</label>
                                    <input
                                        name="experience"
                                        placeholder="Enter years of experience"
                                        value={formData.experience}
                                        onChange={handleChange}
                                        required
                                        className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-2 focus:ring-blue-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-600 mb-1">Annual Salary</label>
                                    <input
                                        name="salary"
                                        placeholder="Enter annual salary"
                                        value={formData.salary}
                                        onChange={handleChange}
                                        required
                                        className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-2 focus:ring-blue-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-600 mb-1">Job Description</label>
                                    <textarea
                                        name="jobDescription"
                                        placeholder="Enter job description"
                                        value={formData.jobDescription}
                                        onChange={handleChange}
                                        required
                                        className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-2 focus:ring-blue-400 h-20"
                                    ></textarea>
                                </div>
                                <div className="flex justify-end gap-3 pt-2 mt-5">
                                    <button
                                        onClick={() => setShowForm(false)}
                                        type="button"
                                        className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:bg-blue-300 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? 'Submitting...' : editingJobId ? 'Update' : 'Submit'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex justify-center items-center">
                        <div className="bg-white w-full max-w-[400px] rounded-xl shadow-2xl p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold text-gray-800">Confirm Deletion</h2>
                                <button
                                    onClick={cancelDelete}
                                    className="text-gray-500 hover:text-red-500 text-sm font-medium"
                                >
                                    <X />
                                </button>
                            </div>
                            <p className="text-sm text-gray-600 mb-6">
                                Are you sure you want to delete this job? This action cannot be undone.
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={cancelDelete}
                                    className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="customer-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Role</th>
                                <th>Experience</th>
                                <th>Salary</th>
                                <th>Description</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(jobs) && jobs.map((job) => (
                                <tr key={job.id}>
                                    <td>{job.role}</td>
                                    <td>{job.experience}</td>
                                    <td>₹{job.salary}</td>
                                    <td>{job.jobDescription}</td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                onClick={() => handleEdit(job)}
                                                className="edit-btn"
                                            >
                                                <Pen size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(job.id)}
                                                className="delete-btn"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CreateJob;